import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// Friendly, customer-facing copy for every error code the stripe
// edge functions (stripe-create-checkout / stripe-customer-portal)
// can emit. We NEVER surface body.error / body.message / status code
// to the customer — those are operator-facing internals. Any code not
// in this map falls back to a per-action generic message at the call
// site.
export const FRIENDLY = {
  billing_not_configured: "Billing isn't set up yet — the app owner needs to add a Stripe key.",
  tier_not_priced:        "This plan isn't available for purchase yet — Stripe Price ID missing.",
  unknown_tier:           "We couldn't find that plan. Please pick a plan again.",
  missing_params:         'Something was missing from that request. Please try again.',
  method_not_allowed:     'Something went wrong starting checkout. Please try again.',
  no_subscription:        "You're not subscribed yet. Pick a plan to get started.",
  unauthorized:           'Please sign in again.',
  forbidden:              "You don't have access to this restaurant.",
  stripe_error:           "We couldn't reach our payment provider just now. Please try again in a moment.",
  internal:               'Something went wrong on our end. Please try again in a moment.',
}

// Drives the SubscriptionCard in Settings + plan selection flow.
//
// Reads the current restaurant's subscription state via the
// current_subscription_summary RPC (joined with billing_tiers so
// the UI can show the display name + monthly_cents). The list of
// available tiers comes from billing_tiers directly so the upgrade
// modal can render plan options.
//
// Pre-Stripe-setup: this hook works even before Scott creates the
// Stripe products. The subscriptions table is empty for everyone,
// so we return null + tiers list, and the UI shows "Start trial"
// CTAs that redirect to the (still-disabled) checkout endpoint.
// Once Stripe is configured, the same flow works end-to-end.
export function useSubscription() {
  const [summary, setSummary]     = useState(null)
  const [tiers, setTiers]         = useState([])
  const [restaurant, setRestaurant] = useState(null)
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Get the active restaurant first — both the summary RPC and
      // the tier list need to know what to scope to.
      const { data: rest, error: restErr } = await supabase
        .from('restaurants')
        .select('id, name, timezone')
        .eq('active', true)
        .order('created_at')
        .limit(1)
        .maybeSingle()
      if (restErr) throw restErr
      setRestaurant(rest ?? null)

      const [summaryRes, tiersRes] = await Promise.all([
        rest
          ? supabase.rpc('current_subscription_summary', { p_restaurant_id: rest.id })
          : Promise.resolve({ data: null, error: null }),
        supabase
          .from('billing_tiers')
          .select('id, display_name, monthly_cents, sort_order, trial_days, stripe_price_id_monthly, stripe_price_id_annual, active')
          .eq('active', true)
          .order('sort_order'),
      ])
      if (summaryRes.error) throw summaryRes.error
      if (tiersRes.error)   throw tiersRes.error
      // RPC returns an array (table-valued) — pull the single row.
      setSummary(Array.isArray(summaryRes.data) ? (summaryRes.data[0] ?? null) : null)
      setTiers(tiersRes.data ?? [])
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useSubscription]', e)
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Derived: is this customer on a paid plan? Trialing counts as
  // "paid" for feature-gating purposes — we don't yank features
  // mid-trial. Canceled and past_due do NOT count.
  const isActive = useMemo(() => {
    if (!summary) return false
    return ['trialing', 'active'].includes(summary.status)
  }, [summary])

  const trialDaysLeft = useMemo(() => {
    if (!summary?.trial_end) return null
    const ms = new Date(summary.trial_end).getTime() - Date.now()
    if (ms < 0) return 0
    return Math.ceil(ms / (1000 * 60 * 60 * 24))
  }, [summary])

  // ---------- mutations ----------

  // Kick off a Stripe Checkout session. Caller is expected to do
  // window.location.href = url after this resolves. Throws with a
  // friendly error on misconfigured Stripe.
  const startCheckout = useCallback(async ({ tierId, billing = 'monthly' }) => {
    if (!restaurant?.id) throw new Error('No active restaurant.')
    const CHECKOUT_GENERIC = "We couldn't reach our payment provider just now. Please try again in a moment."
    let resp, body
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
                       || import.meta.env.VITE_SUPABASE_ANON_KEY
      const token = session?.access_token || anonKey
      resp = await fetch(`${supabaseUrl}/functions/v1/stripe-create-checkout`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify({
          tier_id: tierId,
          billing,
          restaurant_id: restaurant.id,
        }),
      })
      body = await resp.json().catch(() => ({}))
    } catch (e) {
      // fetch rejected (offline/DNS) or getSession threw. Never surface
      // the raw browser/network code — show the per-action generic.
      // eslint-disable-next-line no-console
      console.error('[useSubscription.startCheckout] network/session error', e)
      throw new Error(CHECKOUT_GENERIC)
    }
    if (!resp.ok || body?.error) {
      // eslint-disable-next-line no-console
      console.error('[useSubscription.startCheckout]', resp.status, body)
      const msg = FRIENDLY[body?.error] || "We couldn't start checkout right now. Please try again in a moment."
      // Attach the raw server code for JS-only branching at the call site
      // (e.g. no_subscription reroute). The user-facing message (msg) is
      // unchanged and never includes the raw code/detail.
      const err = new Error(msg)
      err.code = body?.error || null
      throw err
    }
    return body.url
  }, [restaurant?.id])

  // Open the Stripe Customer Portal. Same pattern — caller
  // navigates to the URL.
  const openCustomerPortal = useCallback(async ({ returnPath = '/settings' } = {}) => {
    if (!restaurant?.id) throw new Error('No active restaurant.')
    const PORTAL_GENERIC = "We couldn't open your billing portal right now. Please try again in a moment."
    let resp, body
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
                       || import.meta.env.VITE_SUPABASE_ANON_KEY
      const token = session?.access_token || anonKey
      resp = await fetch(`${supabaseUrl}/functions/v1/stripe-customer-portal`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify({
          restaurant_id: restaurant.id,
          return_path: returnPath,
        }),
      })
      body = await resp.json().catch(() => ({}))
    } catch (e) {
      // fetch rejected (offline/DNS) or getSession threw. Never surface
      // the raw browser/network code — show the per-action generic.
      // eslint-disable-next-line no-console
      console.error('[useSubscription.openCustomerPortal] network/session error', e)
      throw new Error(PORTAL_GENERIC)
    }
    if (!resp.ok || body?.error) {
      // eslint-disable-next-line no-console
      console.error('[useSubscription.openCustomerPortal]', resp.status, body)
      const msg = FRIENDLY[body?.error] || PORTAL_GENERIC
      // Attach the raw server code for JS-only branching at the call site
      // (e.g. no_subscription reroute). The user-facing message (msg) is
      // unchanged and never includes the raw code/detail.
      const err = new Error(msg)
      err.code = body?.error || null
      throw err
    }
    return body.url
  }, [restaurant?.id])

  return {
    summary,
    tiers,
    restaurant,
    isActive,
    trialDaysLeft,
    loading,
    error,
    refresh: fetchAll,
    startCheckout,
    openCustomerPortal,
  }
}
